import React from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, X } from 'lucide-react';
import toast from 'react-hot-toast';
import LicenseStatusBadge from './LicenseStatusBadge';

const formatDate = (dateStr, locale) => {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleString(locale === 'az' ? 'en-US' : locale, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (e) {
    return dateStr;
  }
};

const Field = ({ label, value, mono, emptyText }) => (
  <div>
    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</div>
    <div className={`mt-1 text-sm text-gray-900 ${mono ? 'font-mono' : ''}`}>
      {value || <span className="text-gray-400">{emptyText || '—'}</span>}
    </div>
  </div>
);

const LicenseViewModal = ({ license, onClose, hideTechnicalFields = false }) => {
  const { t, i18n } = useTranslation();

  if (!license) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(license.licenseKey);
      toast.success(t('licences.viewModal.copySuccess'));
    } catch (e) {
      toast.error(t('errors.serverError'));
    }
  };

  const userName = license.user ? `${license.user.firstName} ${license.user.lastName}` : '—';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop" onClick={onClose}>
      <div
        className="modal-content bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">{t('licences.viewModal.title')}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label={t('licences.viewModal.id')} value={license.displayId} mono />
          <Field label={t('licences.viewModal.user')} value={userName} />
          <Field
            label={t('licences.viewModal.plugin')}
            value={license.plugin?.name}
            emptyText="—"
          />

          {!hideTechnicalFields && (
            <>
              <Field
                label={t('licences.viewModal.userTerminalId')}
                value={license.terminalId}
                mono
                emptyText="—"
              />
              <Field
                label={t('licences.viewModal.accountNumber')}
                value={license.accountNumber}
                mono
                emptyText="—"
              />
            </>
          )}

          <div className="md:col-span-2">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {t('licences.viewModal.licenseKey')}
            </div>
            <div className="mt-1 flex items-center gap-2">
              <div className="text-sm text-gray-900 font-mono bg-gray-50 px-3 py-2 rounded border border-gray-200 flex-1 break-all">
                {license.licenseKey}
              </div>
              <button
                type="button"
                onClick={handleCopy}
                className="btn-secondary flex items-center gap-1 px-3 py-2"
                title={t('licences.viewModal.copySuccess')}
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>

          <Field label={t('licences.viewModal.period')} value={t(`licences.period.${license.periodDays}days`)} />
          <Field
            label={t('licences.viewModal.remainingDays')}
            value={`${license.remainingDays ?? 0} days`}
          />

          <div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {t('licences.viewModal.status')}
            </div>
            <div className="mt-1">
              <LicenseStatusBadge status={license.status} />
            </div>
          </div>
          <Field
            label={t('licences.viewModal.createdAt')}
            value={formatDate(license.createdAt, i18n.language)}
          />

          <div className="md:col-span-2">
            <Field
              label={t('licences.viewModal.lastValidatedAt')}
              value={formatDate(license.lastValidatedAt, i18n.language)}
              emptyText={t('licences.viewModal.never')}
            />
          </div>
        </div>

        <div className="flex justify-end p-6 border-t border-gray-200">
          <button onClick={onClose} className="btn-secondary px-4 py-2">
            {t('licences.viewModal.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LicenseViewModal;
